import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, X } from "lucide-react";

/**
 * Full-screen node editor overlay (n8n-style) — opens over the canvas, not inside the node.
 */
const FlowNodeEditorShell = ({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  accent,
  children,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const chip = accent?.chip || "bg-blue-100 text-blue-800";
  const iconCls = accent?.icon || "text-blue-600";

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex flex-col bg-slate-100">
      {/* Top bar */}
      <header className="flex-shrink-0 flex items-center gap-4 px-4 sm:px-6 h-14 bg-white border-b border-gray-200 shadow-sm">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
          Back to canvas
        </button>

        <div className="flex-1 flex items-center gap-3 min-w-0">
          {Icon && (
            <div className={`p-2 rounded-lg ${chip}`}>
              <Icon size={18} className={iconCls} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-base font-bold text-gray-900 truncate">{title}</h1>
            {subtitle && (
              <p className="text-xs text-gray-500 truncate">{subtitle}</p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Close editor"
        >
          <X size={18} />
        </button>
      </header>

      {/* Editor body */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
          {children}
        </div>
      </main>
    </div>,
    document.body
  );
};

export default FlowNodeEditorShell;
